import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';

const CreateOrgModal = ({ onClose, onSave }) => {
  const [form, setForm] = useState({ name: '', org_type: 'barn', location: '', phone: '', email: '', disciplines: '', description: '' });
  const [saving, setSaving] = useState(false);
  const set = useCallback((f) => (e) => { const v = e.target.value; setForm(p => ({ ...p, [f]: v })); }, []);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Name required');
    setSaving(true);
    try {
      const { data } = await api.post('/organizations', form);
      toast.success(`${form.org_type === 'barn' ? 'Barn' : 'Business'} created!`);
      onSave(data);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Create organization</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Type</label>
          <div className="chip-group">
            <div className={`chip ${form.org_type === 'barn' ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, org_type: 'barn' }))}>🏠 Barn</div>
            <div className={`chip ${form.org_type === 'business' ? 'selected' : ''}`} onClick={() => setForm(p => ({ ...p, org_type: 'business' }))}>💼 Training business</div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Name *</label>
          <input className="form-input" value={form.name} onChange={set('name')} placeholder={form.org_type === 'barn' ? 'e.g. Sunrise Stables' : 'e.g. Smith Equestrian Training'} autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Location</label>
          <input className="form-input" value={form.location} onChange={set('location')} placeholder="City, State" autoComplete="off" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="form-group">
            <label className="form-label">Phone</label>
            <input className="form-input" type="tel" value={form.phone} onChange={set('phone')} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={set('email')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Disciplines</label>
          <input className="form-input" value={form.disciplines} onChange={set('disciplines')} placeholder="e.g. Dressage, Hunter/Jumper" autoComplete="off" />
        </div>
        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-input" value={form.description} onChange={set('description')} rows={2} />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleSave} disabled={saving}>{saving ? 'Creating...' : 'Create'}</button>
      </div>
    </div>
  );
};

const JoinOrgModal = ({ prefillCode, onClose, onJoined }) => {
  const [code, setCode] = useState(prefillCode || '');
  const [saving, setSaving] = useState(false);
  const handleJoin = async () => {
    if (!code.trim()) return toast.error('Enter an invite code');
    setSaving(true);
    try {
      const { data } = await api.post('/organizations/join', { invite_code: code });
      toast.success(data.message);
      onJoined();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed'); } finally { setSaving(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
      <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2>Join with invite code</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
        </div>
        <div className="form-group">
          <label className="form-label">Invite code</label>
          <input className="form-input" value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="6-character code" style={{ textTransform: 'uppercase', letterSpacing: 2 }} autoComplete="off" />
        </div>
        <button className="btn btn-primary btn-full" onClick={handleJoin} disabled={saving}>{saving ? 'Sending...' : 'Request to join'}</button>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10, textAlign: 'center' }}>The organization controller will approve your request.</p>
      </div>
    </div>
  );
};

const OrganizationsPage = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);

  const load = () => {
    api.get('/organizations/mine').then(r => setOrgs(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // If arriving via an invite link (?join=CODE), auto-open the join modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('join');
    if (code) setShowJoin(code);
  }, []);

  const statusBadge = (status) => {
    if (status === 'pending') return <span className="badge badge-amber">Pending approval</span>;
    if (status === 'active') return <span className="badge badge-green">Active</span>;
    return null;
  };

  const roleBadge = (role) => {
    if (role === 'manager') return <span className="badge badge-primary">Barn Owner</span>;
    if (role === 'trainer') return <span className="badge badge-purple">Trainer</span>;
    return <span className="badge badge-gray">Member</span>;
  };

  return (
    <div>
      <div className="top-bar">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: 18 }}>Barns & businesses</h1>
            <p style={{ opacity: 0.85, fontSize: 12, marginTop: 2 }}>Your organizations</p>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setShowCreate(true)}>+ Create</button>
          <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowJoin(true)}>Join with code</button>
        </div>

        {loading ? (
          <div className="loading-screen"><div className="spinner" /></div>
        ) : orgs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏠</div>
            <h3>No organizations yet</h3>
            <p>Create a barn or training business, or join one with an invite code.</p>
          </div>
        ) : (
          orgs.map(org => (
            <div key={org.id} className="card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/organizations/${org.id}`)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div className="icon-box" style={{ background: 'var(--cr-primary-light)', fontSize: 20 }}>
                  {org.org_type === 'barn' ? '🏠' : '💼'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{org.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{org.location || (org.org_type === 'barn' ? 'Barn' : 'Training business')}</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                {roleBadge(org.my_role)}
                {statusBadge(org.my_status)}
              </div>
            </div>
          ))
        )}
      </div>

      {showCreate && <CreateOrgModal onClose={() => setShowCreate(false)} onSave={(org) => { setShowCreate(false); load(); navigate(`/organizations/${org.id}`); }} />}
      {showJoin && <JoinOrgModal prefillCode={typeof showJoin === 'string' ? showJoin : ''} onClose={() => setShowJoin(false)} onJoined={() => { setShowJoin(false); load(); }} />}
    </div>
  );
};

export default OrganizationsPage;
